import { Sparkles, Layers } from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CATEGORIES = [
  { id: 'all', name: 'All Services', icon: Sparkles, subCategories: [] },
  { id: 'packages', name: 'Packages', icon: Layers, subCategories: [] },
  { id: 'food', name: 'Food & Catering', icon: () => <span className="text-lg">🍽️</span>, 
    subCategories: ['general', 'breakfast', 'lunch', 'dinner', 'appetizers', 'desserts', 'food-stations', 'buffet'] },
  { id: 'food-trucks', name: 'Food Trucks', icon: () => <span className="text-lg">🚚</span>,
    subCategories: ['general', 'mexican', 'bbq', 'asian', 'pizza', 'desserts', 'breakfast', 'specialty'] },
  { id: 'drinks', name: 'Bar & Beverages', icon: () => <span className="text-lg">🍸</span>,
    subCategories: ['general', 'open-bar', 'beer-wine', 'cocktails', 'non-alcoholic', 'coffee-tea'] },
  { id: 'entertainment', name: 'Entertainment', icon: () => <span className="text-lg">🎵</span>,
    subCategories: ['general', 'dj', 'live-band', 'photo-booth', 'games', 'performers', 'karaoke'] },
  { id: 'staffing', name: 'Staffing', icon: () => <span className="text-lg">👥</span>,
    subCategories: ['general', 'servers', 'bartenders', 'coordinators', 'security', 'valet'] },
  { id: 'logistics', name: 'Logistics', icon: () => <span className="text-lg">🚚</span>,
    subCategories: ['general', 'rentals', 'transportation', 'setup-breakdown', 'av-equipment'] },
  { id: 'decor', name: 'Decor', icon: () => <span className="text-lg">🎈</span>,
    subCategories: ['general', 'florals', 'lighting', 'balloons', 'drapery', 'signage', 'table-decor'] },
  { id: 'photography', name: 'Photography', icon: () => <span className="text-lg">📸</span>,
    subCategories: ['general', 'photographer', 'videographer', 'event-coverage', 'portrait'] },
];

// All admin-creatable categories — same list as CATEGORIES but with the
// virtual 'all' / 'packages' filters removed (those aren't real categories).
export const ADMIN_CATEGORIES = CATEGORIES.filter(c => c.id !== 'all' && c.id !== 'packages')
  .concat([{ id: 'other', name: 'Other', icon: () => <span className="text-lg">✨</span>, subCategories: [] }]);

export const SUB_CATEGORY_LABELS = {
  'breakfast': 'Breakfast', 'lunch': 'Lunch', 'dinner': 'Dinner',
  'appetizers': 'Appetizers', 'desserts': 'Desserts', 'food-stations': 'Food Stations',
  'food-trucks': 'Food Trucks',
  'buffet': 'Buffet', 'open-bar': 'Open Bar', 'beer-wine': 'Beer & Wine',
  'cocktails': 'Cocktails', 'non-alcoholic': 'Non-Alcoholic', 'coffee-tea': 'Coffee & Tea',
  'dj': 'DJ', 'live-band': 'Live Band', 'photo-booth': 'Photo Booth',
  'games': 'Games', 'performers': 'Performers', 'karaoke': 'Karaoke',
  'servers': 'Servers', 'bartenders': 'Bartenders', 'coordinators': 'Coordinators',
  'security': 'Security', 'valet': 'Valet', 'rentals': 'Rentals',
  'transportation': 'Transportation', 'setup-breakdown': 'Setup & Breakdown',
  'av-equipment': 'AV Equipment',
  'florals': 'Florals', 'lighting': 'Lighting', 'balloons': 'Balloons',
  'drapery': 'Drapery', 'signage': 'Signage', 'table-decor': 'Table Decor',
  'mexican': 'Mexican', 'bbq': 'BBQ', 'asian': 'Asian', 'pizza': 'Pizza', 'specialty': 'Specialty',
  'photographer': 'Photographer', 'videographer': 'Videographer',
  'event-coverage': 'Event Coverage', 'portrait': 'Portrait', 'general': 'General',
};

// Customer-facing unit price helper. Single source of truth — use this
// everywhere a customer-facing price is displayed instead of raw base_price.
// Falls back: customer_price → base_price * (1 + customer_markup_rate) → base_price.
export function customerUnitPrice(product) {
  if (!product) return 0;
  if (typeof product.customer_price === 'number' && product.customer_price > 0) {
    return product.customer_price;
  }
  const base = Number(product.base_price) || 0;
  const markupRate = typeof product.customer_markup_rate === 'number' ? product.customer_markup_rate : 0;
  return base * (1 + markupRate);
}

// Format a customer-facing price string with the right unit suffix per pricing model.
export function formatCustomerPrice(product) {
  if (!product) return '';
  const v = customerUnitPrice(product).toFixed(2);
  switch (product.pricing_model) {
    case 'flat_rate': return `$${v}`;
    case 'per_hour': return `$${v}/hr`;
    case 'per_person': return `$${v}/person`;
    case 'per_hour_per_person': return `$${v}/hr/person`;
    default: return `$${v}`;
  }
}

export const PRICING_MODEL_LABELS = {
  flat_rate: 'Flat Rate',
  per_hour: 'Per Hour',
  per_person: 'Per Person',
  per_hour_per_person: 'Per Hour/Person'
};

export const CATEGORY_LABELS = {
  food: 'Food & Catering', drinks: 'Bar & Beverages',
  entertainment: 'Entertainment', staffing: 'Staffing',
  logistics: 'Logistics', decor: 'Decor',
  photography: 'Photography', other: 'Other'
};

export const DURATION_OPTIONS = [
  { hours: 1, label: '1 hour' }, { hours: 1.5, label: '1.5 hours' },
  { hours: 2, label: '2 hours' }, { hours: 2.5, label: '2.5 hours' },
  { hours: 3, label: '3 hours' }, { hours: 3.5, label: '3.5 hours' },
  { hours: 4, label: '4 hours' }, { hours: 4.5, label: '4.5 hours' },
  { hours: 5, label: '5 hours' }, { hours: 6, label: '6 hours' },
  { hours: 7, label: '7 hours' }, { hours: 8, label: '8 hours' },
];
